import styled from '@emotion/styled';
import React from 'react';
import {
  EAutocomplete,
  EAutocompleteOption,
  EAutocompleteProps,
  OnChangeEventType,
} from 'webapp-lib/Components';
import {mixinFlexboxColumn} from 'webapp-lib/styling';

import {AlternativeTagItem, TagsGroup} from '#';
import {StyledTagsGroup} from '#/form/TagsGroup/styles';
import {
  addAsteriskIfRequired,
  combineProps,
  convertPropsForField,
  EFProps,
  useController,
} from '_/utilities/form/controlled-components';

import {CustomOnChangeType} from './types';

type ETagsAutocompleteProps = Omit<EAutocompleteProps, 'value' | 'onChange'> & {
  // type of value has to be `EAutocompleteOption` because of pagination
  value?: EAutocompleteOption[];
  onChange: CustomOnChangeType<EAutocompleteOption[]>;
};

export const ETagsAutocomplete = ({
  label,
  name,
  value = [],
  options = [],
  onChange,
  allowCustomInput,
  ...otherProps
}: ETagsAutocompleteProps): React.ReactElement => {
  const handleSelectionChange = (selectedOptions: EAutocompleteOption[]) => {
    onChange({
      target: {name, value: [...selectedOptions]},
    });
  };

  const handleChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    if (event.type === OnChangeEventType.ClearOnSelection) {
      return;
    }

    const optionValue = event.target.value;
    let option = options.find((o) => o.value === optionValue);

    // if no option is fonnd and we do not allow custom inputs we return
    if (!option) {
      if (!allowCustomInput) {
        return;
      }
      // if no option is given but customeInputs are allowed we set the custom element
      option = {
        value: optionValue,
        displayText: optionValue,
      };
    }

    if (value.some((o) => o.value === option?.value)) {
      handleSelectionChange(value.filter((o) => o.value !== option?.value));
    } else {
      handleSelectionChange([
        ...value,
        {value: option.value, displayText: option.displayText},
      ]);
    }
  };

  const updatedOptions = options.map((option) => ({
    ...option,
    selected: value.map((o) => o.value).includes(option.value),
  }));

  const mapValueToTag = (
    valueOption: EAutocompleteOption
  ): AlternativeTagItem => {
    const option = options.find((it) => it.value === valueOption.value);
    return {
      id: option?.value ?? valueOption.value,
      text: option?.displayText ?? valueOption.displayText ?? valueOption.value,
      closeCallback: () => {
        handleSelectionChange(
          value.filter((o) => o.value !== valueOption.value)
        );
      },
    };
  };

  const tags = [...value].map(mapValueToTag);

  return (
    <StyledTagsSelectWrapper>
      <TagsGroup
        clearButtonDisabled={tags.length === 0}
        label={label}
        showClear={true}
        tags={tags}
        alternativeStyle
        fitToMultiLines
        onClear={() => handleSelectionChange([])}
      />
      <EAutocomplete
        {...otherProps}
        allowCustomInput={allowCustomInput}
        name={name}
        options={updatedOptions}
        multiple
        onChange={handleChange}
      />
    </StyledTagsSelectWrapper>
  );
};

const StyledTagsSelectWrapper = styled.div`
  ${mixinFlexboxColumn};
  gap: var(--gutter-little);

  ${StyledTagsGroup} {
    margin: 0;
  }
`;

export const EFTagsAutocomplete = ({
  name,
  rules,
  shouldUnregister,
  defaultValue,
  control,
  customValue,
  customOnChange,
  ...elementProps
}: EFProps<ETagsAutocompleteProps>): React.ReactElement => {
  const formPropsForField = convertPropsForField(
    useController({name, rules, shouldUnregister, defaultValue, control})
  );
  if (typeof elementProps.label === 'string') {
    elementProps.label = addAsteriskIfRequired(rules, elementProps?.label);
  }
  const combinedProps = combineProps(formPropsForField, elementProps, {
    customValue,
    customOnChange,
  });

  return <ETagsAutocomplete {...combinedProps} />;
};
